import { usd, usdShort } from "./memberModel";
import { IconBack, IconGrid, IconPlus, IconSearch, IconTable } from "./icons";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "admin", label: "Admins" },
  { id: "member", label: "Members" },
  { id: "pending", label: "Pending" },
] as const;

export type MemberFilter = (typeof FILTERS)[number]["id"];
export type MemberLayout = "table" | "cards";

type Counts = {
  total: number;
  admins: number;
  memberRole: number;
  pending: number;
  active: number;
  today: number;
  month: number;
  hasCap: boolean;
  cap: number;
};

type Props = {
  counts: Counts;
  query: string;
  filter: MemberFilter;
  layout: MemberLayout;
  onQuery: (value: string) => void;
  onFilter: (value: MemberFilter) => void;
  onLayout: (value: MemberLayout) => void;
  onClose: () => void;
  onInvite: () => void;
  onOpenUsage?: () => void;
  /** Required so a new render site cannot compile a dead Errors tab. */
  onOpenErrors: () => void;
  onOpenQa?: () => void;
};

export function MembersHeader({ counts, query, filter, layout, onQuery, onFilter, onLayout, onClose, onInvite, onOpenUsage, onOpenErrors, onOpenQa }: Props) {
  const capStat = counts.hasCap ? `/ ${usdShort(counts.cap)} cap` : "no cap set";
  return (
    <div className="members-head">
      <div className="members-head-inner">
        <div className="members-title-row">
          <div className="members-title">
            <h2>Manage users</h2>
            <p>Roles, token budgets, and project access for everyone in the ThunderAy workspace.</p>
          </div>
          <div className="members-head-actions">
            <button className="btn btn-ghost" onClick={onClose}><IconBack /> Back to chat</button>
            <button className="btn btn-accent" onClick={onInvite}><IconPlus /> Invite member</button>
          </div>
        </div>

        <div className="admin-tabs" role="tablist" aria-label="Workspace administration">
          <button role="tab" aria-selected="true" className="on">Users</button>
          <button role="tab" aria-selected="false" onClick={onOpenUsage}>Usage</button>
          <button role="tab" aria-selected="false" onClick={onOpenErrors}>Errors</button>
          {onOpenQa && <button role="tab" aria-selected="false" onClick={onOpenQa}>QA</button>}
        </div>

        <div className="mstats">
          <div className="mstat"><span className="mstat-k">Members</span><span className="mstat-v">{counts.total}<small>{counts.active} active</small></span></div>
          <div className="mstat"><span className="mstat-k">Admins</span><span className="mstat-v">{counts.admins}</span></div>
          <div className="mstat"><span className="mstat-k">Pending 2FA</span><span className="mstat-v">{counts.pending}</span></div>
          <div className="mstat"><span className="mstat-k">Spend · today</span><span className="mstat-v">{usd(counts.today)}</span></div>
          <div className="mstat"><span className="mstat-k">Spend · 30 days</span><span className="mstat-v">{usd(counts.month)}<small>{capStat}</small></span></div>
        </div>

        <div className="mtoolbar">
          <div className="msearch">
            <IconSearch />
            <input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Search name or email…" aria-label="Search members" />
          </div>
          <div className="mfilters" role="tablist" aria-label="Filter members">
            {FILTERS.map((item) => {
              const count = item.id === "all" ? counts.total : item.id === "admin" ? counts.admins : item.id === "member" ? counts.memberRole : counts.pending;
              return <button key={item.id} role="tab" aria-selected={filter === item.id} className={`mfilter${filter === item.id ? " on" : ""}`} onClick={() => onFilter(item.id)}>{item.label}<span className="fc">{count}</span></button>;
            })}
          </div>
          <div className="mtoolbar-spacer" />
          <div className="layout-toggle" role="group" aria-label="Layout">
            <button className={layout === "table" ? "on" : ""} onClick={() => onLayout("table")} aria-pressed={layout === "table"} title="Table view"><IconTable /></button>
            <button className={layout === "cards" ? "on" : ""} onClick={() => onLayout("cards")} aria-pressed={layout === "cards"} title="Card view"><IconGrid /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
